<template>
  <section class="general-settings">
    <div
      v-if="leaveTo"
      class="modal"
      role="dialog"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Você fez algumas alterações.</h5>
            <button
              type="button"
              class="close align-self-start"
              @click="closeBackModal"
            >
              <IconClose />
            </button>
          </div>
          <div class="modal-body">
            Deseja salvar as alterações antes de sair?
          </div>
          <div class="modal-footer">
            <button
              id="leaveUnsavedSettings"
              class="btn btn-secondary btn-white mr-auto"
              @click="leaveUnsaved"
            >
              Descartar as alterações
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              @click="saveAndLeave"
            >
              Salvar as alterações
            </button>
          </div>
        </div>
      </div>
    </div>

    <form
      class="general-settings__content settings-grid"
      @submit.prevent="savePage"
    >
      <slot name="menu"></slot>

      <div
        ref="container"
        class="settings-grid__main custom-scrollbar"
      >
        <div class="settings-grid__main__header">
          <slot name="title"></slot>

          <button
            :disabled="disableSaving"
            class="btn btn-secondary"
          >
            Salvar alterações
          </button>
        </div>

        <Loader
          v-if="loading"
          color="#3E9AA6"
        />
        <div
          v-else
          class="groups"
        >
          <div
            v-for="(group, index) in groups"
            :key="index"
            class="group"
          >
            <label class="group__label">
              {{ group.label }}
              <span
                v-if="group.helpText"
                class="group__label__help-text"
                v-html="group.helpText"
              ></span>
              <span
                v-if="group.warning"
                class="group__label__warning"
                v-html="group.warning"
              ></span>
              <div
                v-if="group.preview"
                class="group__label__preview"
                :style="{
                  '--font-heading': customerSettings.headingFont,
                  '--font-body': customerSettings.bodyFont,
                }"
              >
                <h4 v-if="group.preview.heading">
                  {{ group.preview.heading }}
                </h4>
                <p v-if="group.preview.body">{{ group.preview.body }}</p>
              </div>
            </label>
            <div class="group__model">
              <template v-for="field in group.fields">
                <component
                  :is="UiTypes[field.type]"
                  v-bind="field"
                  :key="`field-${field.name}`"
                  :name="field.name"
                  target-property="customerSettings"
                  :class="{ 'is-invalid': errors[field.name] }"
                />
                <span
                  v-show="errors[field.name]"
                  :key="`error-${field.name}`"
                  class="text-danger"
                  v-html="errors[field.name]"
                >
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import Loader from '@/components/Loader.vue';
import IconClose from '@/components/icons/IconClose.vue';
import Toast from '@/helpers/Toast';
import cloneObject from '@/helpers/cloneObject';
import * as UiTypes from '@/ui-types';

import validate from 'validate.js';

export default {
  name: 'GenericSettings',
  components: {
    IconClose,
    Loader,
  },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      Toast: null,
      statusMesssageDelay: null,
      statusMessage: {
        message: '',
        status: true,
        open: false,
      },
      isSending: false,
      errors: {},
      leaveTo: null,
      unmodifiedSettings: null,
      UiTypes,
    };
  },
  computed: {
    modifiedSettings() {
      if (!this.unmodifiedSettings) {
        return {};
      }

      return Object.entries(this.customerSettings).reduce(
        (acc, [key, value]) =>
          JSON.stringify(this.unmodifiedSettings[key]) !== JSON.stringify(value)
            ? { ...acc, [key]: value }
            : acc,
        {},
      );
    },
    hasErrors() {
      return !!Object.keys(this.errors).length;
    },
    hasChanged() {
      return !!Object.keys(this.modifiedSettings).length;
    },
    disableSaving() {
      return this.hasErrors || !this.hasChanged;
    },
    ...mapState(['loading', 'customerSettings']),
  },
  watch: {
    hasChanged(newValue) {
      this.$emit('update:editing', newValue);
    },
    'customerSettings.bodyFont': function loadFont(font) {
      this.$store.dispatch('loadFont', font);
    },
    'customerSettings.headingFont': function loadFont(font) {
      this.$store.dispatch('loadFont', font);
    },
  },
  mounted() {
    this.unmodifiedSettings = cloneObject(this.customerSettings);

    this.groups.forEach((group) => {
      group.fields.forEach((field) => {
        if (field.validation) {
          this.$watch(`customerSettings.${field.name}`, {
            handler(newValue) {
              const [error] =
                validate(
                  { [field.name]: newValue },
                  { [field.name]: field.validation },
                  { format: 'flat', fullMessages: false },
                ) || [];

              if (error) {
                Vue.set(this.errors, field.name, error);
              } else {
                Vue.delete(this.errors, field.name);
              }
            },
            deep: true,
          });
        }
      });
    });

    this.Toast = new Toast({
      customClass: {
        container: 'feedback-message',
      },
      timer: 4000,
      position: 'top-end',
    });
  },
  beforeDestroy() {
    this.$emit('update:editing', false);
  },
  methods: {
    beforeLeave(to) {
      if (this.hasChanged) {
        if (this.hasErrors) {
          Object.keys(this.errors).forEach((key) => {
            this.customerSettings[key] = this.unmodifiedSettings[key];
          });
        }

        if (Object.keys(this.modifiedSettings).length > 0) {
          this.leaveTo = to;
          return;
        }
      }

      to.next();
    },
    saveAndLeave() {
      this.savePage();
      this.leaveTo.next();
    },
    leaveUnsaved() {
      this.$store.commit('SET_CUSTOMER_SETTINGS', this.unmodifiedSettings);
      this.leaveTo.next();
    },
    closeBackModal() {
      this.leaveTo = null;
    },
    showStatusMessage(info) {
      const classStatus = info.status
        ? 'feedback-message--sucess'
        : 'feedback-message--error';
      const background = info.status ? '#8FF8C1' : '#F7CFD2';

      this.Toast.fire({
        background,
        html: `<span class="${classStatus}">${info.message}</span>`,
      });
    },
    savePage() {
      if (!this.hasErrors) {
        this.$store
          .dispatch('saveCustomerSettings', this.modifiedSettings)
          .then(() => {
            this.showStatusMessage({
              message: 'Configurações salvas com sucesso!',
              status: true,
            });

            this.unmodifiedSettings = cloneObject(this.customerSettings);
          })
          .catch(() => {
            this.showStatusMessage({
              message:
                'Ops... Aconteceu algum erro, tente novamente mais tarde',
              status: false,
            });
          });
      } else {
        this.showStatusMessage({
          message: 'Um ou mais campos preenchidos incorretamente',
          status: false,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.feedback-message {
  margin: 56px 24px;

  .swal2-toast {
    padding: 8px 12px 16px 12px;
  }

  &--sucess {
    color: #000000;
  }

  &--error {
    color: #e0414b;
  }
}
</style>

<style scoped lang="scss">
.general-settings {
  height: 100%;

  .modal {
    z-index: 999999999;
    padding: 1.75rem;
    display: flex;
    background-color: rgba($lb-black, 0.75);

    .modal-dialog {
      margin: auto;
      width: 670px;
      max-width: 100%;
    }
  }

  &__content {
    overflow: hidden;

    .group + .group {
      border-top: 1px solid rgba(0, 0, 0, 0.05);
    }

    .group {
      display: flex;
      padding: 24px 0;
      margin: 0;

      &__label {
        color: $crm-blue-primary;
        font-weight: $lb-medium;
        line-height: 16px;
        width: 45%;
        vertical-align: text-top;
        padding-right: 24px;

        &__help-text,
        &__warning {
          display: block;
          margin-top: 12px;
          font-style: normal;
          font-weight: normal;
          color: #7f7f7f;
        }

        &__warning {
          color: #fa8425;
        }

        &__preview {
          margin-top: 16px;

          & * {
            color: #7f7f7f;
            font-weight: $lb-normal;
          }

          h4 {
            font-family: $lb-heading-font;
            font-size: $size-big;
          }

          p {
            font-family: $lb-content-font;
            font-size: $size-normal;
          }
        }
      }

      &__model {
        width: 55%;
        vertical-align: text-top;

        &::v-deep {
          h3 {
            font-size: $size-small;
          }
        }

        &__label {
          display: block;
          color: $crm-blue-primary;
        }

        &__action {
          padding: 24px 0;
          text-align: right;
        }
      }

      .v-select {
        &[drop-up] {
          ::v-deep {
            .vs__dropdown-toggle {
              border-bottom: 1px solid rgba(60, 60, 60, 0.26);
              border-radius: 4px;
            }

            .vs__dropdown-menu {
              top: auto;
              bottom: calc(100% - 2px);
              border-radius: 4px 4px 0 0;
              border-top-style: solid;
              border-bottom-style: none;
              box-shadow: 0 -3px 6px rgb(0 0 0 / 15%);
            }
          }
        }

        ::v-deep {
          .vs__dropdown-menu {
            max-height: 161px; // 26 * itens + 5
          }

          .vs__dropdown-toggle {
            padding: 4px 0 8px;
          }
        }
      }
    }
  }
}
</style>
